import React, { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import * as XLSX from "xlsx";
import { getToken } from "../../api/auth";
import { getCustomers, getUserById } from "../../api/user";
import { uploadExcelOrderBase64, getSalesHeaderByNo } from "../../api/order";
import "./AddOrder.css";

const getUsernameFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.unique_name || payload.sub || null;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
};

const AddOrder = () => {
  const navigate = useNavigate();
  const token = getToken();
  const username = getUsernameFromToken();

  // State
  const [header, setHeader] = useState({
    customerNo: "",
    customerName: "",
    address: "",
    city: "",
    orderDate: new Date().toISOString().split("T")[0],
    shipmentDate: new Date().toISOString().split("T")[0],
    companyCode: "",
  });

  const [salesLines, setSalesLines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [excelBase64, setExcelBase64] = useState(null);

  // Load user company code on mount
  useEffect(() => {
    const loadCompanyCode = async () => {
      const username = getUsernameFromToken();
      if (!username) return;

      try {
        const user = await getUserById(username);
        if (user?.companyCode) {
          setHeader((prev) => ({ ...prev, companyCode: user.companyCode }));
        } else {
          setHeader((prev) => ({ ...prev, companyCode: "next" }));
        }
      } catch (err) {
        console.error("Failed to load user company code:", err);
        setHeader((prev) => ({ ...prev, companyCode: "next" }));
      }
    };

    if (token) loadCompanyCode();
  }, [token]);

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await getCustomers();
        if (response.success && Array.isArray(response.message)) {
          setCustomers(response.message);
          setFilteredCustomers(response.message);
        } else {
          setError("Failed to fetch customers");
        }
      } catch (err) {
        console.error("Failed to fetch customers:", err);
        setError("Failed to fetch customers. Please try again.");
      }
    };

    if (token) {
      fetchCustomers();
    } else {
      navigate("/login");
    }
  }, [token, navigate]);

  // Handle input changes
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));

    if (name === "customerNo") {
      setSearchQuery(value);
      const filtered = customers.filter(
        (c) =>
          c.no.toLowerCase().includes(value.toLowerCase()) ||
          c.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCustomers(filtered);

      const matched = customers.find((c) => c.no === value);
      if (matched) {
        setHeader((prev) => ({
          ...prev,
          customerName: matched.name,
          address: matched.address || "",
          city: matched.city || "",
        }));
      } else {
        setHeader((prev) => ({
          ...prev,
          customerName: "",
          address: "",
          city: "",
        }));
      }
    }
  };

  const handleCustomerSelect = (customer) => {
    setHeader((prev) => ({
      ...prev,
      customerNo: customer.no,
      customerName: customer.name,
      address: customer.address || "",
      city: customer.city || "",
    }));
    setSearchQuery(customer.no);
    setFilteredCustomers([]);
  };

  // Handle Excel upload + preview
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setSalesLines([]);

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target.result;

        const base64String = btoa(
          new Uint8Array(arrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ""
          )
        );
        setExcelBase64(base64String);

        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const headerRow = parsed[0];
        if (!headerRow || headerRow.length < 2) {
          throw new Error(
            "Excel must have at least 2 columns: Quantity, Barcode"
          );
        }

        const col1 = (headerRow[0] || "").toString().trim().toLowerCase();
        const col2 = (headerRow[1] || "").toString().trim().toLowerCase();

        if (col1 !== "quantity" || col2 !== "barcode") {
          throw new Error(
            `Invalid header. Expected: "Quantity, Barcode". Found: "${headerRow[0]}", "${headerRow[1]}"`
          );
        }

        const lines = parsed.slice(1).map((row, idx) => {
          const qty = Number(row[0]) || 0;
          const barkodi = (row[1] || "").toString().trim();

          if (!barkodi) throw new Error(`Row ${idx + 2}: Barcode is missing`);
          if (qty <= 0) throw new Error(`Row ${idx + 2}: Quantity must be > 0`);

          return {
            lineno: idx + 1,
            no: "PENDING",
            description: "PENDING",
            unitofmeasurecode: "PENDING",
            quantity: qty,
            originalQuantity: qty,
            barkodi,
            unitprice: 0,
            amobilestatus: 0,
            companycode: header.companyCode,
          };
        });

        setSalesLines(lines);
      } catch (err) {
        console.error("Excel processing error:", err);
        setError(err.message || "Failed to process Excel file.");
        setSalesLines([]);
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Failed to read file.");
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // Create order
  const handleCreateOrder = async () => {
    if (!header.customerNo || !excelBase64) {
      setError("Please select a customer and upload an Excel file.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request = {
        customerNo: header.customerNo,
        fileBase64: excelBase64,
        orderDate: header.orderDate,
        shipmentDate: header.shipmentDate || header.orderDate,
      };

      const response = await uploadExcelOrderBase64(request);

      let orderNo = null;

      // Parse order number from { success: true, message: "PO-..." }
      if (response?.success && typeof response.message === "string") {
        orderNo = response.message.trim();
      }

      if (!orderNo) {
        throw new Error("Order number not found in response.");
      }

      // Try to fetch full order details
      try {
        const orderResponse = await getSalesHeaderByNo(orderNo);
        if (orderResponse.success && orderResponse.order) {
          setCreatedOrder({
            documentType: orderResponse.order.documentType,
            no: orderResponse.order.no,
            sellToCustomerNo: orderResponse.order.sellToCustomerNo,
            sellToCustomerName: orderResponse.order.sellToCustomerName,
            sellToAddress: orderResponse.order.sellToAddress,
            sellToCity: orderResponse.order.sellToCity,
            sellToContact: orderResponse.order.sellToContact || "N/A",
            orderDate: orderResponse.order.orderDate,
            documentDate: orderResponse.order.documentDate,
            companyCode: orderResponse.order.companyCode,
            status: orderResponse.order.status,
            saleslines: orderResponse.order.saleslines || [],
          });
        } else {
          throw new Error("Order details not found.");
        }
      } catch (fetchErr) {
        // Order created, but details failed → show green success
        setCreatedOrder({
          no: orderNo,
          successMessage: `Order ${orderNo} created successfully!`,
        });
      }
    } catch (err) {
      console.error("Failed to create order:", err);
      setError(err.message || "Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Status helpers
  const getStatusName = (status) => {
    const statuses = {
      0: "Inserted",
      1: "Assigned to Agent",
      2: "Assigned to Controller",
      3: "Completed",
    };
    return statuses[status] || "Unknown";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "#f59e0b";
      case 1:
        return "#3b82f6";
      case 2:
        return "#8910b9ff";
      case 3:
        return "#22c55e";
      default:
        return "#ffffff";
    }
  };

  const getMobileStatusName = (amobilestatus) => {
    const statuses = {
      0: "Inserted",
      1: "Agent Completed",
      2: "Mistake",
      3: "Approved",
    };
    return statuses[amobilestatus] || "Unknown";
  };

  const getMobileStatusColor = (amobilestatus) => {
    switch (amobilestatus) {
      case 0:
        return "#f59e0b";
      case 1:
        return "#3b82f6";
      case 2:
        return "#b91b10ff";
      case 3:
        return "#22c55e";
      default:
        return "#ffffff";
    }
  };

  // Redirect if not logged in
  if (!token) return <Navigate to="/login" replace />;

  // SUCCESS: Order created, but no full details
  if (createdOrder?.successMessage) {
    return (
      <div className="add-order-container">
        <div className="add-order-page-header">
          <h2 className="add-order-title">Order Created</h2>
          <Link
            to="/orders"
            className="add-order-glass-btn add-order-btn-primary add-order-back-btn"
          >
            <FaArrowLeft size={18} /> Back to Orders
          </Link>
        </div>

        <div
          className="add-order-error-message"
          style={{
            background: "rgba(34, 197, 94, 0.2)",
            color: "#22c55e",
            border: "1px solid #22c55e",
            boxShadow: "0 0 12px rgba(34, 197, 94, 0.5)",
            animation: "cardPopIn 0.6s ease-out",
          }}
        >
          {createdOrder.successMessage}
        </div>
      </div>
    );
  }

  // SUCCESS: Full order details
  if (createdOrder) {
    return (
      <div className="add-order-container">
        <div className="add-order-page-header">
          <h2 className="add-order-title">Order Created: {createdOrder.no}</h2>
          <Link
            to="/orders"
            className="add-order-glass-btn add-order-btn-primary add-order-back-btn"
          >
            <FaArrowLeft size={18} /> Back to Orders
          </Link>
        </div>

        <div className="add-header-cards-container">
          {/* Customer Card */}
          <div className="add-header-card">
            <h3 className="card-title">Customer Information</h3>
            <div className="add-header-field">
              <label>Customer:</label>
              <span>{createdOrder.sellToCustomerName || "N/A"}</span>
            </div>
            <div className="add-header-field">
              <label>Customer No:</label>
              <span>{createdOrder.sellToCustomerNo}</span>
            </div>
            <div className="add-header-field">
              <label>Address:</label>
              <span>{createdOrder.sellToAddress || "N/A"}</span>
            </div>
            <div className="add-header-field">
              <label>City:</label>
              <span>{createdOrder.sellToCity || "N/A"}</span>
            </div>
            {createdOrder.sellToContact &&
              createdOrder.sellToContact !== "N/A" && (
                <div className="add-header-field">
                  <label>Contact:</label>
                  <span>{createdOrder.sellToContact}</span>
                </div>
              )}
          </div>

          {/* Order Info Card */}
          <div className="add-header-card">
            <h3 className="card-title">Order Information</h3>
            <div className="add-header-field">
              <label>Order Date:</label>
              <span>
                {new Date(createdOrder.orderDate).toLocaleDateString()}
              </span>
            </div>
            <div className="add-header-field">
              <label>Document Date:</label>
              <span>
                {new Date(createdOrder.documentDate).toLocaleDateString()}
              </span>
            </div>
            <div className="add-header-field">
              <label>Status:</label>
              <span
                style={{
                  backgroundColor: getStatusColor(createdOrder.status),
                  padding: "2px 8px",
                  borderRadius: "4px",
                  color: "#fff",
                }}
              >
                {getStatusName(createdOrder.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Sales Lines */}
        <div className="add-lines-container">
          <h3 className="add-lines-title">Sales Lines</h3>
          {createdOrder.saleslines.length > 0 ? (
            <table className="add-lines-table">
              <thead>
                <tr>
                  <th>Line No</th>
                  <th>Product No</th>
                  <th>Description</th>
                  <th>Unit</th>
                  <th>Quantity</th>
                  <th>Original Quantity</th>
                  <th>Barcode</th>
                  <th>Mobile Status</th>
                  <th>Unit Price</th>
                </tr>
              </thead>
              <tbody>
                {createdOrder.saleslines.map((line, idx) => (
                  <tr
                    key={idx}
                    className={`row-animate row-animate-${Math.min(idx, 3)} ${
                      Number(line.quantity) !== Number(line.originalQuantity)
                        ? "highlight-row"
                        : ""
                    }`}
                  >
                    <td>{line.lineno}</td>
                    <td>{line.no}</td>
                    <td>{line.description || "N/A"}</td>
                    <td>{line.unitofmeasurecode || "N/A"}</td>
                    <td>{Number(line.quantity).toFixed(2)}</td>
                    <td>{Number(line.originalQuantity).toFixed(2)}</td>
                    <td>{line.barkodi || "N/A"}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor: getMobileStatusColor(
                            line.amobilestatus
                          ),
                          padding: "2px 8px",
                          borderRadius: "4px",
                          color: "#fff",
                        }}
                      >
                        {getMobileStatusName(line.amobilestatus)}
                      </span>
                    </td>
                    <td>€{Number(line.unitprice).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="add-order-empty-state">
              <p>No sales lines found</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // INPUT FORM
  return (
    <div className="add-order-container">
      <div className="add-order-page-header">
        <h2 className="add-order-title">Add New Order</h2>
        <div className="add-order-header-buttons">
          <Link
            to="/orders"
            className="add-order-glass-btn add-order-btn-primary add-order-back-btn"
          >
            <FaArrowLeft size={18} /> Back to Orders
          </Link>
          <button
            className="add-order-glass-btn add-order-btn-primary add-order-create-btn"
            onClick={handleCreateOrder}
            disabled={loading || !header.customerNo || !excelBase64}
          >
            {loading ? "Creating..." : "Create Order"}
          </button>
        </div>
      </div>

      {error && <div className="add-order-error-message">{error}</div>}

      <div className="add-header-cards-container">
        {/* Customer Card */}
        <div className="add-header-card">
          <h3 className="card-title">Customer Information</h3>
          <div className="add-header-field">
            <label>Customer No:</label>
            <div style={{ position: "relative", width: "60%" }}>
              <input
                type="text"
                name="customerNo"
                value={searchQuery}
                onChange={handleHeaderChange}
                placeholder="Search by Customer No or Name"
                className="add-order-customer-input"
              />
              {searchQuery && filteredCustomers.length > 0 && (
                <ul className="add-order-customer-search-dropdown">
                  {filteredCustomers.map((customer) => (
                    <li
                      key={customer.no}
                      className="add-order-customer-search-item"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      {customer.no} - {customer.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="add-header-field">
            <label>Customer:</label>
            <span>{header.customerName || "Select a customer"}</span>
          </div>
          <div className="add-header-field">
            <label>Address:</label>
            <span>{header.address || "N/A"}</span>
          </div>
          <div className="add-header-field">
            <label>City:</label>
            <span>{header.city || "N/A"}</span>
          </div>
        </div>

        {/* Order Info Card */}
        <div className="add-header-card">
          <h3 className="card-title">Order Information</h3>
          <div className="add-header-field">
            <label>Order Date:</label>
            <input
              type="date"
              name="orderDate"
              value={header.orderDate}
              onChange={handleHeaderChange}
              className="add-order-date-input"
            />
          </div>
          <div className="add-header-field">
            <label>Shipment Date:</label>
            <input
              type="date"
              name="shipmentDate"
              value={header.shipmentDate}
              onChange={handleHeaderChange}
              className="add-order-date-input"
            />
          </div>
          <div className="add-header-field">
            <label>Sales Lines Count:</label>
            <span>{salesLines.length}</span>
          </div>
          <div className="add-header-field">
            <label>Company Code:</label>
            <span>{header.companyCode}</span>
          </div>
        </div>
      </div>

      {/* Excel Import */}
      <div className="add-lines-container">
        <h3 className="add-lines-title">Sales Lines (Import from Excel)</h3>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleExcelUpload}
          className="add-order-file-input"
        />
        {loading && (
          <div className="add-order-loading-container">
            <div className="add-order-spinner"></div>
            <p>Processing Excel...</p>
          </div>
        )}
        {salesLines.length > 0 ? (
          <table className="add-lines-table">
            <thead>
              <tr>
                <th>Line No</th>
                <th>Product No</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Original Quantity</th>
                <th>Barcode</th>
                <th>Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {salesLines.map((line, idx) => (
                <tr
                  key={idx}
                  className={`row-animate row-animate-${Math.min(idx, 3)}`}
                >
                  <td>{line.lineno}</td>
                  <td>{line.no}</td>
                  <td>{line.description || "N/A"}</td>
                  <td>{line.unitofmeasurecode || "N/A"}</td>
                  <td>{Number(line.quantity).toFixed(2)}</td>
                  <td>{Number(line.originalQuantity).toFixed(2)}</td>
                  <td>{line.barkodi || "N/A"}</td>
                  <td>€{Number(line.unitprice).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="add-order-empty-state">
            <p>No sales lines imported yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOrder;
